import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, LanguageSwitcher } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { Package, Send, ChevronRight, User } from 'lucide-react';

export default function Welcome() {
  const navigate = useNavigate();
  const { t, isGeorgian } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Handle QR code redirects
  useEffect(() => {
    const device = searchParams.get('device');
    const action = searchParams.get('action');
    if (device && action === 'delivery') {
      navigate(`/send?device=${device}`);
    } else if (device && action === 'pickup') {
      navigate(`/pickup?device=${device}`);
    }
  }, [searchParams, navigate]);

  return (
    <div style={s.page}>
      <div style={s.glow1} /><div style={s.glow2} />

      <div style={s.container}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.brand}>
            <img src="/damitove-logo.png" alt="Damitove" style={{height: 48, width: 'auto'}} />
            <span style={s.brandName}>Dami<span style={{ color: '#8BC63E' }}>tove</span></span>
          </div>
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            {user && (
              <div
                onClick={() => navigate('/profile')}
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8BC63E, #00b4d8)',
                  color: '#07080c',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 800, cursor: 'pointer',
                }}
                title={isGeorgian ? 'ჩემი პროფილი' : 'My Profile'}
              >
                {(user.first_name?.[0] || user.email?.[0] || 'U').toUpperCase()}
              </div>
            )}
            <LanguageSwitcher style={{ background: '#161821', border: '1px solid #252733', color: '#8789a0' }} />
          </div>
        </div>

        {/* Hero */}
        <div style={s.hero}>
          <h1 style={s.heroTitle}>
            {isGeorgian ? 'სმარტ საფოსტო ყუთები' : 'Smart Locker Service'}
          </h1>
          <p style={s.heroSub}>
            {isGeorgian
              ? 'გაგზავნეთ ან მიიღეთ ამანათი უსაფრთხო სმარტ ბოქსით'
              : 'Send or receive parcels with secure smart lockers'}
          </p>
        </div>

        {/* Two Cards */}
        <div className='welcome-cards' style={s.cards}>
          <div style={s.card} onClick={() => navigate('/delivery')}>
            <div style={s.cardIconWrap}>
              <Send size={32} color="#8BC63E" />
            </div>
            <h2 style={s.cardTitle}>
              {isGeorgian ? 'ამანათის დატოვება' : 'Delivery'}
            </h2>
            <p style={s.cardDesc}>
              {isGeorgian
                ? 'მოათავსეთ ამანათი სმარტ ბოქსში და გაუგზავნეთ მიმღებს კოდი'
                : 'Store a parcel in a smart locker and send the pickup code to recipient'}
            </p>
            <div style={s.cardAction}>
              {isGeorgian ? 'დაწყება' : 'Get Started'} <ChevronRight size={16} />
            </div>
          </div>

          <div style={s.card} onClick={() => navigate('/pickup')}>
            <div style={{ ...s.cardIconWrap, background: 'rgba(77,144,254,0.1)' }}>
              <Package size={32} color="#4d90fe" />
            </div>
            <h2 style={s.cardTitle}>
              {isGeorgian ? 'გატანა' : 'Pickup'}
            </h2>
            <p style={s.cardDesc}>
              {isGeorgian
                ? 'შეიყვანეთ გატანის კოდი და ამოიღეთ ამანათი ბოქსიდან'
                : 'Enter your pickup code and retrieve your parcel from the locker'}
            </p>
            <div style={{ ...s.cardAction, color: '#4d90fe' }}>
              {isGeorgian ? 'კოდის შეყვანა' : 'Enter Code'} <ChevronRight size={16} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={s.footer}>
          <span style={s.footerText}>© 2026 Damitove</span>
          <span style={s.footerDot}>·</span>
          <a href="https://damitove.ge" style={s.footerLink}>damitove.ge</a>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: '#07080c', color: '#e4e5ed',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', overflow: 'hidden',
  },
  glow1: { position: 'absolute', top: '-20%', left: '-15%', width: '50%', height: '50%', background: 'radial-gradient(circle, rgba(139,198,62,0.06) 0%, transparent 70%)', pointerEvents: 'none' },
  glow2: { position: 'absolute', bottom: '-20%', right: '-15%', width: '50%', height: '50%', background: 'radial-gradient(circle, rgba(77,144,254,0.04) 0%, transparent 70%)', pointerEvents: 'none' },
  container: { width: '100%', maxWidth: 560, padding: '40px 20px', position: 'relative', zIndex: 1 },

  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 48 },
  brand: { display: 'flex', alignItems: 'center', gap: 10 },
  logo: { width: 42, height: 42, background: 'linear-gradient(135deg, #8BC63E, #00b4d8)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 19, color: '#07080c' },
  brandName: { fontWeight: 800, fontSize: 24, color: '#e4e5ed', letterSpacing: -0.5 },

  hero: { textAlign: 'center', marginBottom: 40 },
  heroTitle: { fontSize: 26, fontWeight: 800, letterSpacing: -0.5, marginBottom: 10 },
  heroSub: { fontSize: 15, color: '#8789a0', lineHeight: 1.6 },

  cards: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  card: {
    background: '#0f1117', border: '1px solid #252733', borderRadius: 18,
    padding: 28, cursor: 'pointer', transition: 'all 0.2s',
  },
  cardIconWrap: {
    width: 60, height: 60, borderRadius: 16,
    background: 'rgba(139,198,62,0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 18,
  },
  cardTitle: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  cardDesc: { fontSize: 14, color: '#8789a0', lineHeight: 1.6, marginBottom: 18 },
  cardAction: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: '#8BC63E' },

  footer: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 48 },
  footerText: { fontSize: 12, color: '#8a8da4' },
  footerDot: { color: '#8a8da4' },
  footerLink: { fontSize: 12, color: '#8a8da4', textDecoration: 'none' },
};
